import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import styles from "./styles.module.css";
import Stepper from "../../../../components/stepper";
import colors from "../../../../constants/colors";
import { StateContext, StatusContext } from "../../../../contexts";
import { Link, useNavigate } from "react-router-dom";
import routes from "../../../../routes";
import rnt from "../../../../assets/images/rnt.png";
import fiscalSituation from "../../../../assets/images/fiscal_situation.png";
import domicile from "../../../../assets/images/domicile.png";
import officialIdentification from "../../../../assets/images/official_identification.png";
import API, { getFileUrl } from "../../../../api";

function DocumentExample(props) {
  const { type } = props;

  let source = null;

  if (type === "rnt") {
    source = rnt;
  } else if (type === "fiscal_situation") {
    source = fiscalSituation;
  } else if (type === "domicile") {
    source = domicile;
  } else if (type === "official_identification") {
    source = officialIdentification;
  }

  return (
    <VStack flex={1} space={3} alignItems="center">
      <Heading color={colors.secondary} fontSize="md">
        Tu documento se tiene que ver así
      </Heading>
      {source && <Image src={source} alt={type} width={220} height={170} />}
      <Text fontSize="sm">Verifica que tu documento sea legible</Text>
    </VStack>
  );
}

function SignUpAgencyDocumentsPage() {
  const navigate = useNavigate();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [rnt, setRnt] = useState(null);
  const [fiscalSituation, setFiscalSituation] = useState(null);
  const [domicile, setDomicile] = useState(null);
  const [officialIdentification, setOfficialIdentification] = useState(null);
  const [constitutiveAct, setConstitutiveAct] = useState(null);
  const [notarialPower, setNotarialPower] = useState(null);
  const [authorizationLetter, setAuthorizationLetter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onSave = async () => {
    try {
      setIsLoading(true);
      const [
        _rnt,
        _fiscalSituation,
        _domicile,
        _officialIdentification,
        _constitutiveAct,
        _notarialPower,
        _authorizationLetter,
      ] = await Promise.all(
        [
          rnt,
          fiscalSituation,
          domicile,
          officialIdentification,
          constitutiveAct,
          notarialPower,
          authorizationLetter,
        ].map(async (file) => {
          if (!file) {
            return null;
          }
          const formData = new FormData();
          formData.append("files", file);
          const {
            data: [data],
          } = await API(state.jwt).post("/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return data.id;
        }),
      );
      const data = Object.fromEntries(
        Object.entries({
          rnt: _rnt,
          fiscalSituation: _fiscalSituation,
          domicile: _domicile,
          officialIdentification: _officialIdentification,
          constitutiveAct: _constitutiveAct,
          notarialPower: _notarialPower,
          authorizationLetter: _authorizationLetter,
          agency: state.user.agency.id,
        }).filter(([key, value]) => key && value != null),
      );
      if (state.user?.agency?.documents?.id) {
        await API(state.jwt).put(
          `/agency-documents/${state.user.agency.documents.id}`,
          { data },
        );
      } else {
        await API(state.jwt).post("/agency-documents", { data });
      }
      const user = await state.refreshUser();
      state.updateIsLoggedIn(true);
      if (user.missingAgencyDocuments) {
        navigate(routes.signUpAgencyPending);
      } else {
        navigate(routes.signUpAgencySuccess);
      }
    } catch (error) {
      setIsLoading(false);
      status.updateErrorMessage(error);
    }
  };

  return (
    <VStack paddingTop={50} alignItems="center">
      <Helmet>
        <title>Registro - Adjunta los documentos solicitados</title>
      </Helmet>
      <Heading color={colors.secondary} fontSize="2xl" textAlign="center">
        Registrarse
      </Heading>
      <Stepper step={4} />
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}>
        <VStack
          backgroundColor={colors.white}
          padding={10}
          borderRadius={5}
          space={5}>
          <Heading color={colors.secondary} fontSize="xl">
            Adjunta los documentos
            <br />
            solicitados
          </Heading>
          <Divider height="2px" backgroundColor={colors.secondary} />
          <HStack space={5} marginY={5}>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Registro Nacional de Turismo (RNT)
                <br />o Folio del tramite
              </Heading>
              {state.user?.agency?.documents?.rnt?.url && (
                <a
                  href={getFileUrl(state.user.agency.documents.rnt.url)}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setRnt(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <DocumentExample type="rnt" />
          </HStack>
          <Divider height="1px" backgroundColor={colors.primary} />
          <HStack space={5} marginY={5}>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Constancia de Situación Fiscal
              </Heading>
              {state.user?.agency?.documents?.fiscalSituation?.url && (
                <a
                  href={getFileUrl(
                    state.user.agency.documents.fiscalSituation.url,
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setFiscalSituation(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <DocumentExample type="fiscal_situation" />
          </HStack>
          <Divider height="1px" backgroundColor={colors.primary} />
          <HStack space={5} marginY={5}>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Verificación de Domicilio
              </Heading>
              {state.user?.agency?.documents?.domicile?.url && (
                <a
                  href={getFileUrl(state.user.agency.documents.domicile.url)}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setDomicile(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <DocumentExample type="domicile" />
          </HStack>
          <Divider height="1px" backgroundColor={colors.primary} />
          <HStack space={5} marginY={5}>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Identificación Oficial: INE,
                <br />
                Pasaporte, Cédula profesional
              </Heading>
              <Text fontSize="sm">Adjuntar documentos de frente y reverso</Text>
              {state.user?.agency?.documents?.officialIdentification?.url && (
                <a
                  href={getFileUrl(
                    state.user.agency.documents.officialIdentification.url,
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setOfficialIdentification(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <DocumentExample type="official_identification" />
          </HStack>
          <Divider height="1px" backgroundColor={colors.primary} />
          <HStack marginY={10} space={5}>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Acta constitutiva
              </Heading>
              <Text fontSize="sm">
                Verifica que tu documento sea legible antes de subirlo
              </Text>
              {state.user?.agency?.documents?.constitutiveAct?.url && (
                <a
                  href={getFileUrl(
                    state.user.agency.documents.constitutiveAct.url,
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setConstitutiveAct(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Poder Notarial
              </Heading>
              <Text fontSize="sm">
                Verifica que tu documento sea legible antes de subirlo
              </Text>
              {state.user?.agency?.documents?.notarialPower?.url && (
                <a
                  href={getFileUrl(
                    state.user.agency.documents.notarialPower.url,
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setNotarialPower(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              <Heading color={colors.gray} fontSize="md">
                Carta autorización
              </Heading>
              <Text fontSize="sm">
                Verifica que tu documento sea legible antes de subirlo
              </Text>
              {state.user?.agency?.documents?.authorizationLetter?.url && (
                <a
                  href={getFileUrl(
                    state.user.agency.documents.authorizationLetter.url,
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text underline>Ver documento cargado</Text>
                </a>
              )}
              <input
                type="file"
                onChange={(e) => setAuthorizationLetter(e.target.files[0])}
                disabled={isLoading}
              />
            </label>
          </HStack>
          <VStack space={5} marginBottom={10}>
            <Checkbox
              defaultIsChecked
              colorScheme="danger"
              isDisabled={isLoading}>
              <Text color={colors.gray}>
                Acepto el uso de mi información personal y la recolección de la
                información de pasajeros para uso operacional de acuerdo al{" "}
                <Link
                  to="/aviso_de_privacidad.pdf"
                  target="_blank"
                  rel="noopener noreferrer">
                  <Text
                    borderBottomColor={colors.primary}
                    borderBottomWidth={1}
                    color={colors.primary}>
                    Aviso de Privacidad
                  </Text>
                </Link>
              </Text>
            </Checkbox>
            <Checkbox
              defaultIsChecked
              colorScheme="danger"
              isDisabled={isLoading}>
              <Text color={colors.gray}>
                Quiero recibir publicidad y las mejores promociones y descuentos
                de Autotur.
              </Text>
            </Checkbox>
          </VStack>
          <button disabled={isLoading}>
            <Box
              backgroundColor={colors.primary}
              paddingX={20}
              paddingY={2}
              borderRadius="full">
              <Text color={colors.white}>Continuar</Text>
            </Box>
          </button>
        </VStack>
      </form>
    </VStack>
  );
}

DocumentExample.propTypes = {
  type: PropTypes.string.isRequired,
};

export default SignUpAgencyDocumentsPage;
